import { useEffect, useRef, useState } from "react";
import { Mail, X } from "lucide-react";

export const WaitlistModal = ({ open, onClose }) => {
  const hostRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);

  const handleClose = () => {
    setSubmitted(false);
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSubmitted(false);
    window._form_callback = () => setSubmitted(true);

    const mount = async () => {
      try {
        const res = await fetch("/ac-waitlist.html");
        const html = await res.text();
        if (cancelled || !hostRef.current) return;
        const doc = new DOMParser().parseFromString(html, "text/html");
        const nodes = Array.from(doc.querySelectorAll("style, form, script"));
        const host = hostRef.current;
        host.replaceChildren();
        nodes.forEach((node) => {
          if (node.tagName === "SCRIPT") {
            const s = document.createElement("script");
            s.textContent = node.textContent;
            host.appendChild(s);
          } else {
            host.appendChild(document.importNode(node, true));
          }
        });
        const buttonWrapper = host.querySelector(
          "#_form_53_ ._button-wrapper",
        );
        if (buttonWrapper) {
          const note = document.createElement("div");
          note.className = "mt-3 text-xs leading-relaxed text-[#756A63]";
          note.setAttribute("data-testid", "ac-privacy-note");
          note.innerHTML =
            'Mit Anmeldung für die Insiderliste erklärst Du Dich damit einverstanden, E-Mails von mir rund um Success on Your Terms und den Newsletter zu erhalten. Du kannst Deine Zustimmung jederzeit widerrufen. <a href="https://luisabergholz.com/datenschutzerklaerung" target="_blank" rel="noopener noreferrer" class="underline underline-offset-2 transition-colors hover:text-[#EDD98F]" data-testid="ac-privacy-link">Datenschutzerklärung</a>';
          buttonWrapper.parentNode.insertBefore(note, buttonWrapper);
        }
      } catch (e) {
        console.error("ActiveCampaign form could not be loaded", e);
      }
    };
    const handleAcSubmit = async (e) => {
      const form = e.target;
      if (!form || form.id !== "_form_53_") return;
      e.preventDefault();
      e.stopPropagation();
      const emailInput = form.querySelector('input[name="email"]');
      const email = emailInput ? emailInput.value.trim() : "";
      const btn = form.querySelector("#_form_53_submit");
      const resetBtn = () => {
        if (btn) {
          btn.disabled = false;
          btn.classList.remove("processing");
        }
      };
      if (
        !/^[\+_a-z0-9-'&=]+(\.[\+_a-z0-9-']+)*@[a-z0-9-]+(\.[a-z0-9-]+)*(\.[a-z]{2,})$/i.test(
          email,
        )
      ) {
        window._show_error?.(53, "Bitte gib eine gültige E-Mail-Adresse ein.");
        resetBtn();
        return;
      }
      if (btn) {
        btn.disabled = true;
        btn.classList.add("processing");
      }
      try {
        const res = await fetch(
          "https://luisabergholz.activehosted.com/proc.php?jsonp=true",
          {
            method: "POST",
            headers: { Accept: "application/json" },
            body: new FormData(form),
          },
        );
        const text = await res.text();
        let js = text;
        try {
          const parsed = JSON.parse(text);
          if (parsed && parsed.js) js = parsed.js;
        } catch {
          // Antwort ist kein JSON, dann direkt als JS interpretieren
        }
        if (js.includes("_show_error")) {
          window.eval(js);
          resetBtn();
        } else {
          setSubmitted(true);
        }
      } catch {
        window._show_error?.(
          53,
          "Ihre Übermittlung konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",
        );
        resetBtn();
      }
    };
    const host = hostRef.current;
    if (host) host.addEventListener("submit", handleAcSubmit, true);

    mount();

    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      cancelled = true;
      delete window._form_callback;
      if (host) host.removeEventListener("submit", handleAcSubmit, true);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="waitlist-modal"
    >
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-sm"
        onClick={handleClose}
        data-testid="waitlist-modal-overlay"
      />
      <div className="relative w-full max-w-md border border-gold bg-[#15100E] p-7 shadow-2xl sm:p-9">
        <span className="pointer-events-none absolute left-3 top-3 h-4 w-4 border-l border-t border-gold" />
        <span className="pointer-events-none absolute right-3 top-3 h-4 w-4 border-r border-t border-gold" />
        <span className="pointer-events-none absolute bottom-3 left-3 h-4 w-4 border-b border-l border-gold" />
        <span className="pointer-events-none absolute bottom-3 right-3 h-4 w-4 border-b border-r border-gold" />
        <button
          onClick={handleClose}
          aria-label="Schließen"
          data-testid="waitlist-modal-close"
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center border border-gold text-[#F4F0EB] transition-colors hover:bg-gold hover:text-[#1a1207]"
        >
          <X className="h-4 w-4" />
        </button>
        <p className="font-heading text-xs uppercase tracking-[0.4em] text-gold">
          Insiderliste
        </p>
        {submitted ? (
          <div className="mt-8 pb-2 text-center" data-testid="waitlist-success">
            <div className="mx-auto flex h-12 w-12 items-center justify-center border border-gold text-[#EDD98F]">
              <Mail className="h-5 w-5" />
            </div>
            <p className="mt-6 font-heading text-2xl font-bold text-[#F4F0EB] sm:text-3xl">
              Fast geschafft!
            </p>
            <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-[#A39790] sm:text-base">
              Bitte schau als nächstes in Dein Postfach und bestätige noch kurz
              Deine E-Mail-Adresse.
            </p>
            <button
              onClick={handleClose}
              data-testid="waitlist-success-close"
              className="mt-8 inline-flex items-center justify-center bg-gold px-8 py-3.5 font-heading text-sm font-bold uppercase tracking-wider text-[#1a1207] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:glow-gold"
            >
              Alles klar
            </button>
          </div>
        ) : (
          <>
            <h3 className="mt-3 pr-10 font-heading text-2xl font-bold leading-snug text-[#F4F0EB] sm:text-3xl">
              Setz Dich auf die Insiderliste
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-[#A39790]">
              Trage Deinen Namen und Deine E-Mail-Adresse ein, um Dich auf die
              Insiderliste für Success on Your Terms einzutragen ♟️🃏🔥
            </p>
            <div
              ref={hostRef}
              className="no-scrollbar mt-6 max-h-[46vh] overflow-y-auto"
              data-testid="ac-form-container"
            />
            <p className="mt-6 border-t border-[rgba(237,217,143,0.25)] pt-5 text-xs leading-relaxed text-[#A39790] sm:text-sm">
              Du verpflichtest Dich an dieser Stelle zu nichts. Als Insider wirst Du
              vor allen anderen über Neuigkeiten rund um Success on Your Terms
              informiert.
            </p>
          </>
        )}
      </div>
    </div>
  );
};
